"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";

import { useToast } from "@/components/ui/toast";

/**
 * The one way to leave a screen after writing something.
 *
 * Two pieces of knowledge used to live in call sites, and each was forgotten at least once:
 *
 * 1. **wait for the toast.** A message the person never reads is a message that was not sent. The
 *    host owns the timers, so it is the only thing that can answer whether something is still
 *    asking to be read (`whenRead`) — and an error never holds, because an error means the person
 *    is staying;
 * 2. **`refresh()` before `push()`.** Both are guards that read what was just written. The push
 *    alone replays whatever the client Router Cache holds for the address — a decision taken
 *    before the write. Refreshing *after* only re-reads the page that stale decision already
 *    landed on. This cost us the curation sending the host back to the curation (staging,
 *    2026-10-08) and the terms landing on the event step of a list a consultant had finished.
 *
 * The veil with the monogram needs no coordination: it is triggered by the router's own dispatch
 * (`onRouterTransitionStart` → `announcePageTransition`), so it starts when the push starts. The
 * order the product asked for — message, then monogram, then the next screen — falls out of
 * waiting *before* the push, and nothing here has to know the veil exists.
 */
export function useLeaveFor(): (href: string) => Promise<void> {
  const router = useRouter();
  const { whenRead } = useToast();

  /*
   * The hold is a real gap in time, and the person can leave during it — the browser's own Back,
   * the header's wordmark, a tab. Navigating after that would drag them out of wherever they
   * went, so the move is abandoned if this screen is gone.
   */
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  return useCallback(
    async (href: string) => {
      await whenRead();
      if (!isMounted.current) return;

      router.refresh();
      router.push(href);
    },
    [router, whenRead],
  );
}
